import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "api/baseurl";
import { toast } from "react-toastify";

// @mui material components
import Card from "@mui/material/Card";

// Custom components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Authentication layout
import BasicLayout from "layouts/authentication/components/BasicLayout";

// Encryption utilities
import { encryptBody } from "encryptionAndDecryption/bodyEncryption";
import { decryptBody } from "encryptionAndDecryption/bodyDecryption";
import { fetchSecretKey } from "encryptionAndDecryption/secretKeyDecryption";

// Background Image
import bgImage from "assets/images/bg-profile.jpeg";

function ResetPassword() {

  const location = useLocation();

  const navigate = useNavigate();
  const [formData, setFormData] = useState({
      username: location.state?.username || "",
    password: "",
    passwordConfirm: "",
  });
  const [passwordFeedback, setPasswordFeedback] = useState([]);
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  // --- Password Rule Checker ---
  const getPasswordFeedback = (password) => {
    const feedback = [];

    if (!password || password.length < 8) feedback.push("Minimum 8 characters");
    if (!/[A-Z]/.test(password)) feedback.push("At least one uppercase letter");
    if (!/[a-z]/.test(password)) feedback.push("At least one lowercase letter");
    if (!/[0-9]/.test(password)) feedback.push("At least one number");
    if (!/[@$!%*?&]/.test(password)) feedback.push("At least one special character (@$!%*?&)");
    
    const sequentialPattern = /(012|123|234|345|456|567|678|789|890|abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|987|876|765|654|543|432|321|210|zyx|yxw|xwv|wvu|vut|uts|tsr|srq|rqp|qpo|pon|onm|nml|mlk|lkj|kji|jih|ihg|hgf|gfe|fed|edc|dcb|cba)/;
    if (sequentialPattern.test(password)) feedback.push("Cannot contain sequential characters");

    const repeatPattern = /(.)\1{1,}/;
    if (repeatPattern.test(password)) feedback.push("Cannot contain repeated consecutive characters");

    return feedback;
  };

  // --- Input Change Handler ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === "password") {
      setPasswordFeedback(getPasswordFeedback(value));
        if (formData.passwordConfirm) {
      setConfirmPasswordError(value !== formData.passwordConfirm ? "Passwords do not match" : "");
    }
  }

  if (name === "passwordConfirm") {
    setConfirmPasswordError(value !== formData.password ? "Passwords do not match" : "");
  }
};

  // --- Submit Handler ---
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (passwordFeedback.length > 0) {
      toast.error("Password does not meet all security rules.");
      return;
    }

    if (formData.password !== formData.passwordConfirm) {
      toast.error("Passwords do not match!");
      return;
    }

    try {
      const secretKey = await fetchSecretKey();
      const encryptedData = encryptBody(formData, secretKey);

      const response = await axios.post("/api/resetPassword", encryptedData, {
        headers: { "Content-Type": "text/plain" },
      });

      const decryptedResponse = decryptBody(response.data, secretKey);
      if (!decryptedResponse) throw new Error("Decryption failed, no data returned.");

      if (decryptedResponse.success) {
        toast.success("Password set successfully!");
        setTimeout(() => navigate("/"), 100);
      } else {
        toast.error(decryptedResponse?.message || "Failed to reset password.");
      }
    } catch (error) {
      console.error("Reset Password Error:", error);
      toast.error(error.response?.data || "Reset password failed");
    }
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox pt={8} pb={3} px={3}>
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <img
              className="mb-3"
               src={process.env.REACT_APP_IMAGE_PATH}
              alt="logo"
              style={{ height: "55px", marginTop: "-10px" }}
            />
          </div>

          <MDBox pt={4} pb={3} px={3}>
            <MDBox component="form" role="form" onSubmit={handleSubmit}>
              {/* Username */}
              <MDBox mb={2}>
                <MDInput
                  type="text"
                  label="Username"
                  fullWidth
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                    disabled
                />
              </MDBox>

              {/* Password */}
              <MDBox mb={2}>
                <MDInput
                  type="password"
                  label="New Password"
                  fullWidth
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
                {formData.password && (
                  <ul style={{ color: "red", marginTop: "5px", fontSize: "13px" }}>
                    {passwordFeedback.length === 0 ? (
                      <li style={{ color: "green" }}>All rules satisfied ✅</li>
                    ) : (
                      passwordFeedback.map((rule, index) => <li key={index}>{rule}</li>)
                    )}
                  </ul>
                )}
              </MDBox>

              {/* Confirm Password */}
             <MDBox mb={2}>
  <MDInput
    type="password"
    label="Confirm New Password"
    fullWidth
    name="passwordConfirm"
    value={formData.passwordConfirm}
    onChange={handleChange}
    required
  />
  {confirmPasswordError && (
    <div style={{ color: "red", fontSize: "13px", marginTop: "5px" }}>
      {confirmPasswordError}
    </div>
  )}
</MDBox>


              {/* Buttons */}
              <MDBox display="flex" alignItems="center" mt={3}>
                <MDTypography
                  variant="button"
                  color="info"
                  onClick={() => navigate("/")}
                  sx={{ cursor: "pointer", fontWeight: "bold", mr: "auto" }}
                >
                  Return to Login
                </MDTypography>

                <MDButton
                  type="submit"
                  variant="gradient"
                  sx={{
                    px: 3,
                    py: 1,
                    fontSize: "14px",
                    borderRadius: "10px",
                    backgroundColor: "#2e7faeff",
                    color: "#FFFFFF !important",
                    fontWeight: 600,
                    "&:hover": {
                      backgroundColor: "#1b4f9c",
                    },
                  }}
                >
                  Reset Password
                </MDButton>
              </MDBox>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default ResetPassword;
